// src/components/Chatbot/Chatbot.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { getCurrentUser } from "@/services/currentUserService";

// Types
interface Message {
  id: number;
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
}

interface QuickReply {
  icon: string;
  label: string;
  text: string;
}

interface UserDetails {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  phone_number: string;
  timezone: string;
  plan: number;
  plan_name: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  pause_count: number;
  active_addons: string[];
  total_campaign_limit: number;
  campaigns_sent_this_month: number;
  active_addons_count: number;
  avatar: string;
  company_logo: string;
  status: string;
  rollover_credits: number;
  rollover_expires_at: string | null;
  test_campaigns: number;
  test_campaigns_expires_at: string | null;
  is_superuser: boolean;
  is_staff: boolean;
  get_total_campaign_limit: number;
  get_remaining_campaigns: number;
  associations_used: number;
  association_limit: number;
  remaining_associations: number;
  has_used_free_trial: boolean;
  is_in_free_trial: boolean;
  trial_campaigns_limit: number;
  trial_campaigns_sent: number;
  is_in_active_trial: boolean;
  remaining_trial_campaigns: number;
}

interface ChatbotAPIRequest {
  message: string;
  sessionId: string;
  timestamp: string;
  sender: string;
  userContext?: {
    isAuthenticated: boolean;
    userId?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    planName?: string;
    status?: string;
    remainingCampaigns?: number;
    totalCampaignLimit?: number;
  };
}

interface ChatbotAPIResponse {
  output: string;
  userDetails?: UserDetails;
}

// ── Props ────────────────────────────────────────────────────────
interface ChatbotProps {
  // When true, the Chatbot's own FAB toggle button is hidden.
  // Open/close is controlled externally via isOpen + onClose.
  hideFab?: boolean;
  // Externally controlled open state (used when hideFab=true).
  isOpen?: boolean;
  // Called when the Chatbot's internal minimize/close button is clicked.
  onClose?: () => void;
}

// Generate unique session ID
const generateSessionId = (): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomStr}`;
};

// Get or create session ID from localStorage
const getSessionId = (): string => {
  const storageKey = "chatbot_session_id";
  let sessionId = localStorage.getItem(storageKey);
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(storageKey, sessionId);
  }
  return sessionId;
};

// API Configuration
const API_CONFIG = {
  chatbotURL: import.meta.env.VITE_CHATBOT_URL || "https://mylistiq.app.n8n.cloud/webhook/rag",
  timeout: 30000,
};

// User API Service
const userAPI = {
  fetchUserDetails: async (): Promise<UserDetails | null> => {
    const hasToken =
      sessionStorage.getItem("access_token") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token");

    if (!hasToken) return null;

    try {
      const data = await getCurrentUser();
      return data as UserDetails;
    } catch (error) {
      console.error("Error fetching user details:", error);
      return null;
    }
  },
};

// Chatbot API Service
const chatbotAPI = {
  sendMessage: async (
    message: string,
    sessionId: string,
    userDetails: UserDetails | null
  ): Promise<{ output: string; userDetails?: UserDetails }> => {
    const userContext = userDetails
      ? {
        isAuthenticated: true,
        userId: userDetails.id,
        email: userDetails.email,
        firstName: userDetails.first_name,
        lastName: userDetails.last_name,
        company: userDetails.company,
        planName: userDetails.plan_name,
        status: userDetails.status,
        remainingCampaigns: userDetails.get_remaining_campaigns,
        totalCampaignLimit: userDetails.get_total_campaign_limit,
      }
      : { isAuthenticated: false };

    const requestBody: ChatbotAPIRequest = {
      message,
      sessionId,
      timestamp: new Date().toISOString(),
      sender: "user",
      userContext,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

      const response = await fetch(API_CONFIG.chatbotURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data: ChatbotAPIResponse[] = await response.json();

      if (Array.isArray(data) && data.length > 0 && data[0].output) {
        return { output: data[0].output, userDetails: userDetails || undefined };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") throw new Error("Request timed out. Please try again.");
        throw error;
      }
      throw new Error("An unexpected error occurred");
    }
  },
};

// Quick replies configuration
const quickReplies: QuickReply[] = [
  { icon: "👋", label: "Hello!", text: "Hello!" },
  { icon: "📞", label: "Contact", text: "What is the contact information for mylistiq?" },
  { icon: "💰", label: "Pricing", text: "Provide all pricing plans." },
  { icon: "🚀", label: "Features", text: "What features does MyListIQ offer?" },
  { icon: "❓", label: "Help", text: "How can I get started?" },
];

const getQuickReplies = (_userDetails: UserDetails | null): QuickReply[] => quickReplies;

const Chatbot: React.FC<ChatbotProps> = ({
  hideFab = false,
  isOpen: isOpenProp,
  onClose,
}) => {
  // Internal open state — used only when hideFab=false (standalone mode).
  const [isOpenInternal, setIsOpenInternal] = useState(false);

  // Resolve the effective open state.
  // When hideFab=true, the parent controls it via isOpenProp.
  const isOpen = hideFab ? (isOpenProp ?? false) : isOpenInternal;

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState<string>(getSessionId);
  const [error, setError] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [hasFetchedUser, setHasFetchedUser] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch user + set greeting the first time the chat opens
  useEffect(() => {
    if (!isOpen || hasFetchedUser) return;

    const fetchUser = async () => {
      setIsLoadingUser(true);
      setHasFetchedUser(true);
      const user = await userAPI.fetchUserDetails();
      setUserDetails(user);
      setIsLoadingUser(false);

      const greeting = user
        ? `Hello${user.first_name ? `, ${user.first_name}` : ""}! 👋 Welcome back to MyListIQ support. You're on the ${user.plan_name} plan with ${user.get_remaining_campaigns} launches remaining. How can I help you today?`
        : "Hello! 👋 Welcome to MyListIQ support. How can I help you today?";

      setMessages([{ id: 1, text: greeting, sender: "bot", timestamp: new Date() }]);
    };

    fetchUser();
  }, [isOpen, hasFetchedUser]);

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isTyping, scrollToBottom]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleToggle = () => {
    if (hideFab) {
      // Controlled mode: delegate close to parent
      onClose?.();
    } else {
      setIsOpenInternal((prev) => !prev);
    }
  };

  const addMessage = useCallback((text: string, sender: "user" | "bot") => {
    setMessages((prev) => [...prev, { id: Date.now(), text, sender, timestamp: new Date() }]);
  }, []);

  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim()) return;
    setError(null);
    addMessage(messageText, "user");
    setInputValue("");
    setIsTyping(true);

    try {
      const response = await chatbotAPI.sendMessage(messageText, sessionId, userDetails);
      setIsTyping(false);
      addMessage(response.output, "bot");
    } catch (err) {
      setIsTyping(false);
      const errorMessage = err instanceof Error ? err.message : "Sorry, I'm having trouble responding. Please try again.";
      setError(errorMessage);
      addMessage("I apologize, but I'm experiencing technical difficulties. Please try again in a moment.", "bot");
    }
  }, [addMessage, sessionId, userDetails]);

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); sendMessage(inputValue); };
  const handleQuickReply = (text: string) => { sendMessage(text); };
  const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const clearChat = () => {
    const greeting = userDetails
      ? `Hello${userDetails.first_name ? `, ${userDetails.first_name}` : ""}! 👋 Chat has been cleared. How can I help you today?`
      : "Hello! 👋 Chat has been cleared. How can I help you today?";
    setMessages([{ id: Date.now(), text: greeting, sender: "bot", timestamp: new Date() }]);
    setError(null);
  };

  const retryLastMessage = () => {
    const lastUserMessage = [...messages].reverse().find((m) => m.sender === "user");
    if (lastUserMessage) {
      setMessages((prev) => prev.slice(0, -1));
      sendMessage(lastUserMessage.text);
    }
  };

  const currentQuickReplies = getQuickReplies(userDetails);

  return (
    <div className={hideFab ? "font-sans" : "fixed bottom-5 right-5 z-[9999] font-sans"}>
      {/* FAB toggle — hidden when controlled externally */}
      {!hideFab && (
        <button
          onClick={handleToggle}
          className="
            w-14 h-14 rounded-full shadow-lg
            bg-gradient-to-r from-blue-600 to-purple-600
            hover:from-blue-700 hover:to-purple-700
            flex items-center justify-center
            transition-all duration-300 ease-in-out
            hover:scale-110 hover:shadow-xl
            focus:outline-none focus:ring-4 focus:ring-blue-300
          "
          aria-label="Toggle Chatbot"
        >
          {isOpen ? (
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
            </svg>
          )}
          {!isOpen && messages.length > 1 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">!</span>
          )}
        </button>
      )}

      {/* Chat window */}
      <div
        className={`
          ${hideFab
            ? "relative"  // Inside BotLauncher — no absolute positioning needed
            : "absolute bottom-16 right-0 left-0 sm:left-auto mx-4 sm:mx-0"
          }
          w-[calc(100vw-1.5rem)] max-w-[400px] h-[min(550px,calc(100vh-6rem))]
          bg-white dark:bg-gray-900
          rounded-2xl shadow-2xl
          flex flex-col overflow-hidden
          ${hideFab
            ? (isOpen ? "flex" : "hidden")   // Parent controls visibility
            : `transition-all duration-300 ease-in-out ${isOpen ? "opacity-100 visible translate-y-0 scale-100" : "opacity-0 invisible translate-y-4 scale-95"}`
          }
        `}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 text-white sm:px-5 sm:py-4">
          <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-white/20 sm:h-11 sm:w-11">
            {userDetails?.avatar ? (
              <img src={userDetails.avatar} alt={userDetails.first_name} className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            ) : (
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-sm leading-tight sm:text-base">MyListIQ Support</h3>
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-white/80 sm:text-xs">
              <span className="h-2 w-2 flex-shrink-0 rounded-full bg-green-400 animate-pulse"></span>
              {userDetails ? (
                <>{userDetails.plan_name} Plan • {userDetails.get_remaining_campaigns} launches left</>
              ) : (
                "Online | AI-Powered Support"
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 pl-2">
            <button onClick={clearChat}
              className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Clear chat" title="Clear chat">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <button onClick={handleToggle}
              className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Minimize chat">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 13H5v-2h14v2z" />
              </svg>
            </button>
          </div>
          </div>
        </div>

        {/* User Info Banner */}
        {userDetails && (
          <div className="bg-blue-50 dark:bg-blue-900/30 px-4 py-2 border-b border-blue-200 dark:border-blue-800">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex flex-wrap items-center gap-2">
              <span className="text-xs text-blue-600 dark:text-blue-400">👤 {userDetails.email}</span>
              <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-full">
                {userDetails.plan_name}
              </span>
            </div>
            <span className={`w-fit text-xs px-2 py-0.5 rounded-full ${userDetails.status === "Active" ? "bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300" : "bg-yellow-100 dark:bg-yellow-800 text-yellow-700 dark:text-yellow-300"}`}>
              {userDetails.status}
            </span>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800 px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              {error}
            </span>
            <button onClick={retryLastMessage} className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium underline">
              Retry
            </button>
          </div>
        )}

        {/* Quick Replies */}
        <div className="flex flex-wrap gap-2 px-3 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sm:px-4">
          {currentQuickReplies.map((reply, index) => (
            <button key={index} onClick={() => handleQuickReply(reply.text)}
              disabled={isTyping || isLoadingUser}
              className="flex min-h-10 items-center gap-1 rounded-full border border-blue-500 bg-white px-3 py-2 text-xs font-medium text-blue-600 transition-all duration-200 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-blue-400">
              <span>{reply.icon}</span>{reply.label}
            </button>
          ))}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50 dark:bg-gray-800 space-y-3">
          {isLoadingUser && messages.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2 text-gray-500">
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm">Loading...</span>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div key={message.id} className={`flex gap-2 animate-fadeIn ${message.sender === "user" ? "flex-row-reverse" : "flex-row"}`}>
              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden ${message.sender === "bot" ? "bg-gradient-to-r from-blue-600 to-purple-600" : "bg-gray-300 dark:bg-gray-600"}`}>
                {message.sender === "bot" ? (
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                  </svg>
                ) : userDetails?.avatar ? (
                  <img src={userDetails.avatar} alt={userDetails.first_name} className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                )}
              </div>
              <div className="max-w-[85%] sm:max-w-[75%] flex flex-col">
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${message.sender === "bot" ? "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-sm shadow-sm" : "bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-br-sm"}`}>
                  {message.text}
                </div>
                <span className={`text-[10px] text-gray-400 mt-1 px-1 ${message.sender === "user" ? "text-right" : "text-left"}`}>
                  {formatTime(message.timestamp)}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-2 animate-fadeIn">
              <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" />
                </svg>
              </div>
              <div className="bg-white dark:bg-gray-700 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
                <span className="text-[10px] text-gray-400 mt-1 block">AI is thinking...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="px-3 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 sm:px-4">
          <div className="flex items-center gap-2 sm:gap-3">
          <input ref={inputRef} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
            placeholder={userDetails ? `Ask anything, ${userDetails.first_name}...` : "Type your message..."}
            disabled={isTyping || isLoadingUser}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-full text-sm bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all" />
          <button type="submit" disabled={!inputValue.trim() || isTyping || isLoadingUser}
            className="w-11 h-11 flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Send message">
            {isTyping ? (
              <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-center border-t border-gray-200 dark:border-gray-700">
          <span className="text-[10px] sm:text-xs text-gray-400">
            Powered by MyListIQ AI 
          </span>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default Chatbot;
